/*
  Product photos full screen (snippets/pradem-lightbox.liquid). Dawn's gallery buttons call show(button);
  the photo with the button's data-media-id opens.

  One photo at a time, fitted to the screen. Zoom: wheel (towards the pointer), a click (double tap on phones),
  the +/− buttons or keys, two fingers. A zoomed photo moves by dragging and never leaves the screen.
  Next/previous: arrows, ← →, swipe, thumbnails. Esc, the × or a click beside the photo close it.
*/
if (!customElements.get('pradem-lightbox')) {
  const MAX_ZOOM = 5;
  const STEP = 1.6;

  customElements.define(
    'pradem-lightbox',
    class PrademLightbox extends HTMLElement {
      connectedCallback() {
        if (this.slides) return;
        // Out of the product section, so no transform or overflow of the page can clip it; moving it calls
        // this again, and that call sets it up.
        if (this.parentElement !== document.body) {
          document.body.append(this);
          return;
        }
        this.slides = [...this.querySelectorAll('.pradem-lightbox__slide')];
        this.thumbs = [...this.querySelectorAll('.pradem-lightbox__thumb')];
        this.stage = this.querySelector('[data-stage]');
        this.level = this.querySelector('[data-zoom-reset]');
        this.index = 0;
        this.view = { scale: 1, x: 0, y: 0 };
        this.pointers = new Map();

        this.querySelector('[data-close]').addEventListener('click', () => this.hide());
        this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
        this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
        this.querySelector('[data-zoom-in]').addEventListener('click', () => this.zoomTo(this.view.scale * STEP));
        this.querySelector('[data-zoom-out]').addEventListener('click', () => this.zoomTo(this.view.scale / STEP));
        this.level.addEventListener('click', () => this.zoomTo(1));
        this.thumbs.forEach((thumb) => thumb.addEventListener('click', () => this.go(+thumb.dataset.index)));
        this.addEventListener('keydown', (event) => this.key(event));
        this.stage.addEventListener('wheel', (event) => this.wheel(event), { passive: false });
        this.stage.addEventListener('pointerdown', (event) => this.down(event));
        this.stage.addEventListener('pointermove', (event) => this.move(event));
        this.stage.addEventListener('pointerup', (event) => this.up(event));
        this.stage.addEventListener('pointercancel', (event) => this.up(event));
        window.addEventListener('resize', () => !this.hidden && this.apply());
      }

      show(opener) {
        const id = opener?.getAttribute('data-media-id');
        const index = this.slides.findIndex((slide) => slide.dataset.mediaId === id);
        if (!this.slides.length) return;
        this.opener = opener;
        this.hidden = false;
        document.body.classList.add('overflow-hidden');
        document.documentElement.classList.add('pradem-lightbox-open');
        this.go(Math.max(0, index), false);
        requestAnimationFrame(() => this.classList.add('is-open'));
        this.querySelector('[data-close]').focus({ preventScroll: true });
      }

      hide() {
        this.classList.remove('is-open');
        this.hidden = true;
        document.body.classList.remove('overflow-hidden');
        document.documentElement.classList.remove('pradem-lightbox-open');
        this.reset();
        this.opener?.focus({ preventScroll: true });
      }

      go(index, animate = true) {
        const count = this.slides.length;
        this.index = (index + count) % count;
        this.reset();
        this.slides.forEach((slide, i) => {
          slide.hidden = i !== this.index;
          slide.classList.toggle('is-entering', animate && i === this.index);
        });
        // The photo shown and its neighbours load; the rest waits.
        for (const offset of [0, 1, -1]) this.load(this.slides[(this.index + offset + count) % count]);
        this.thumbs.forEach((thumb, i) => {
          const current = i === this.index;
          thumb.setAttribute('aria-current', String(current));
          if (current) thumb.scrollIntoView({ block: 'nearest', inline: 'center', behavior: animate ? 'smooth' : 'auto' });
        });
        this.querySelector('[data-count]').textContent = `${this.index + 1} ${this.dataset.of} ${count}`;
      }

      load(slide) {
        const image = slide?.querySelector('img');
        if (!image || image.src) return;
        image.srcset = image.dataset.srcset;
        image.src = image.dataset.src;
      }

      image() {
        return this.slides[this.index]?.querySelector('img');
      }

      /* ----- Zoom and pan: the photo's transform; it stays inside the screen ----- */
      reset() {
        this.view = { scale: 1, x: 0, y: 0 };
        this.apply();
      }

      // Size of the photo fitted to the screen at 100 % (layout size, before the zoom transform).
      fitted() {
        const image = this.image();
        return { width: image?.offsetWidth || 0, height: image?.offsetHeight || 0, box: this.stage.getBoundingClientRect() };
      }

      clamp() {
        const { width, height, box } = this.fitted();
        const limitX = Math.max(0, (width * this.view.scale - box.width) / 2);
        const limitY = Math.max(0, (height * this.view.scale - box.height) / 2);
        this.view.x = Math.min(limitX, Math.max(-limitX, this.view.x));
        this.view.y = Math.min(limitY, Math.max(-limitY, this.view.y));
      }

      apply() {
        const image = this.image();
        if (!image) return;
        this.clamp();
        image.style.transform = `translate(${this.view.x}px, ${this.view.y}px) scale(${this.view.scale})`;
        this.classList.toggle('is-zoomed', this.view.scale > 1.01);
        this.level.textContent = `${Math.round(this.view.scale * 100)}%`;
        this.querySelector('[data-zoom-out]').disabled = this.view.scale <= 1.001;
        this.querySelector('[data-zoom-in]').disabled = this.view.scale >= MAX_ZOOM - 0.001;
      }

      // Zoom keeping the point under (clientX, clientY) in place; the stage's centre by default.
      zoomTo(scale, clientX, clientY) {
        const next = Math.min(MAX_ZOOM, Math.max(1, scale));
        const box = this.stage.getBoundingClientRect();
        const px = clientX == null ? 0 : clientX - (box.left + box.width / 2);
        const py = clientY == null ? 0 : clientY - (box.top + box.height / 2);
        const ratio = next / this.view.scale;
        this.view.x = px - (px - this.view.x) * ratio;
        this.view.y = py - (py - this.view.y) * ratio;
        this.view.scale = next;
        this.apply();
      }

      wheel(event) {
        if (event.target.closest('button')) return;
        event.preventDefault();
        const delta = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
        this.zoomTo(this.view.scale * Math.exp(-delta * 0.0018), event.clientX, event.clientY);
      }

      /* ----- Pointers: drag to pan, swipe to change photo, two fingers to zoom, tap beside the photo closes ----- */
      down(event) {
        if (event.target.closest('button')) return;
        try {
          this.stage.setPointerCapture(event.pointerId);
        } catch {
          // The pointer is already gone; the gesture still ends on pointerup.
        }
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (this.pointers.size === 1) {
          this.gesture = { x: event.clientX, y: event.clientY, viewX: this.view.x, viewY: this.view.y, moved: false, time: performance.now() };
        } else if (this.pointers.size === 2) {
          const [a, b] = [...this.pointers.values()];
          this.pinch = { distance: Math.hypot(a.x - b.x, a.y - b.y), scale: this.view.scale };
        }
      }

      move(event) {
        if (!this.pointers.has(event.pointerId)) return;
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (this.pinch && this.pointers.size === 2) {
          const [a, b] = [...this.pointers.values()];
          this.zoomTo((this.pinch.scale * Math.hypot(a.x - b.x, a.y - b.y)) / this.pinch.distance, (a.x + b.x) / 2, (a.y + b.y) / 2);
          if (this.gesture) this.gesture.moved = true;
          return;
        }
        const gesture = this.gesture;
        if (!gesture) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        if (Math.hypot(dx, dy) > 6) gesture.moved = true;
        if (this.view.scale > 1.01) {
          this.view.x = gesture.viewX + dx;
          this.view.y = gesture.viewY + dy;
          this.apply();
        } else if (gesture.moved) {
          // Not zoomed: the photo follows the finger sideways as a hint of the swipe.
          this.image().style.transform = `translateX(${dx}px)`;
        }
      }

      up(event) {
        if (!this.pointers.has(event.pointerId)) return;
        this.pointers.delete(event.pointerId);
        if (this.pointers.size) return;
        const gesture = this.gesture;
        this.gesture = null;
        if (this.pinch) {
          this.pinch = null;
          return;
        }
        if (!gesture) return;
        const dx = event.clientX - gesture.x;
        const dy = event.clientY - gesture.y;
        if (this.view.scale <= 1.01 && gesture.moved) {
          if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) && this.slides.length > 1) this.go(this.index + (dx < 0 ? 1 : -1));
          else if (dy > 120 && Math.abs(dy) > Math.abs(dx)) this.hide();
          else this.apply();
          return;
        }
        if (gesture.moved) return;
        // The stage captures the pointer, so the release's target is the stage: hit-test the photo's box.
        const box = this.image()?.getBoundingClientRect();
        const onPhoto = Boolean(box && event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom);
        // Mouse: a click on the photo zooms in there (or back out); a click beside it closes.
        if (event.pointerType === 'mouse') {
          if (onPhoto) this.zoomTo(this.view.scale > 1.01 ? 1 : 2.5, event.clientX, event.clientY);
          else if (this.view.scale <= 1.01) this.hide();
          return;
        }
        // Touch: a double tap zooms; a single tap beside the photo closes.
        const now = performance.now();
        if (event.pointerType !== 'mouse' && this.lastTap && now - this.lastTap < 300) {
          this.lastTap = 0;
          this.zoomTo(this.view.scale > 1.01 ? 1 : 2.5, event.clientX, event.clientY);
          return;
        }
        this.lastTap = now;
        if (this.view.scale <= 1.01 && !onPhoto) {
          clearTimeout(this.tapTimer);
          this.tapTimer = setTimeout(() => {
            if (this.lastTap === now) this.hide();
          }, 300);
        }
      }

      key(event) {
        const actions = {
          Escape: () => this.hide(),
          ArrowLeft: () => this.view.scale > 1.01 ? this.pan(80, 0) : this.go(this.index - 1),
          ArrowRight: () => this.view.scale > 1.01 ? this.pan(-80, 0) : this.go(this.index + 1),
          ArrowUp: () => this.pan(0, 80),
          ArrowDown: () => this.pan(0, -80),
          '+': () => this.zoomTo(this.view.scale * STEP),
          '=': () => this.zoomTo(this.view.scale * STEP),
          '-': () => this.zoomTo(this.view.scale / STEP),
          0: () => this.zoomTo(1),
        };
        if (event.key === 'Tab') {
          this.trap(event);
          return;
        }
        const action = actions[event.key];
        if (!action) return;
        event.preventDefault();
        action();
      }

      pan(dx, dy) {
        this.view.x += dx;
        this.view.y += dy;
        this.apply();
      }

      // Tab stays inside the open photo view.
      trap(event) {
        const focusable = [...this.querySelectorAll('button:not([disabled])')].filter((element) => element.offsetParent);
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }
  );
}
